import { IgTypography } from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { MarkdownText } from "@src/components/ai-elements/markdown-text";

export interface ScreenshotEvidenceImageProps {
  description: string;
  href?: string | null;
  maxHeight?: string | number;
  maxWidth?: string | number;
  objectFit?: "contain" | "cover";
  src: string;
  surface?: string | null;
}

export const ScreenshotEvidenceImage = ({
  description,
  href,
  maxHeight = "320px",
  maxWidth = "100%",
  objectFit = "contain",
  src,
  surface,
}: ScreenshotEvidenceImageProps) => {
  const theme = useTheme();
  const igTheme = theme as typeof theme & {
    igBorders?: { core?: { default?: string } };
  };
  const borderColor = igTheme.igBorders?.core?.default ?? theme.palette.divider;
  const textPalette = theme.palette.text as typeof theme.palette.text & {
    tertiary?: string;
  };
  const tertiaryTextColor = textPalette.tertiary ?? textPalette.secondary;
  // Cover fills a fixed frame and crops; contain shows the whole screenshot, so
  // the image keeps its own aspect ratio and the frame shrinks to fit it.
  const isCover = objectFit === "cover";
  const image = (
    <Box
      alt={description}
      component="img"
      src={src}
      sx={{
        display: "block",
        height: isCover ? maxHeight : "auto",
        maxHeight,
        maxWidth: "100%",
        objectFit,
        objectPosition: "center center",
        width: isCover ? "100%" : "auto",
      }}
    />
  );

  return (
    <Stack
      alignItems="center"
      direction="column"
      spacing="6px"
      sx={{ width: "100%" }}
    >
      <Box
        component={href ? "a" : "div"}
        href={href ?? undefined}
        rel={href ? "noreferrer" : undefined}
        target={href ? "_blank" : undefined}
        sx={{
          border: `1px solid ${borderColor}`,
          borderRadius: "8px",
          display: "block",
          maxWidth,
          mx: "auto",
          overflow: "hidden",
          width: isCover ? "100%" : "fit-content",
        }}
      >
        {image}
      </Box>
      {surface ? (
        <IgTypography
          variant="body2"
          sx={{
            color: tertiaryTextColor,
            fontSize: "11px",
          }}
        >
          {surface}
        </IgTypography>
      ) : null}
      <MarkdownText>{description}</MarkdownText>
    </Stack>
  );
};
